<template>
  <main class="app-container">
    <section class="profile loader-block-parent">
      <div v-show="isLoading" class="loader-block">
        <div>
          <div class="lds-ring">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </div>
      <div class="profile__top">
        <h2>{{ $t("project_settings.title") }}</h2>
        <router-link
          v-if="auth.user.role != Role.Admin"
          class="complete-later"
          :to="{
            name: 'project-details',
            params: { id: route.params.id, activeTab: 'proposal' },
          }"
          >{{ $t("project_settings.complete_later") }}</router-link
        >
      </div>
      <div class="complete-later"></div>
      <team :project="projectDetails" />
      <div class="project__settings__rooms settings-section">
        <p class="settings-section__heading">
          {{ $t("project_settings.rooms") }}
        </p>
        <div class="settings-section__button">
          <button
            data-a11y-dialog-show="add-room-dialog"
            @click.prevent="showAddRoomDialog(room)"
          >
            <svg
              id="add-24px"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path
                id="Path_42"
                data-name="Path 42"
                d="M0,0H24V24H0Z"
                fill="none"
              />
              <path
                id="Path_43"
                data-name="Path 43"
                fill="#FF7366"
                d="M19,13H13v6H11V13H5V11h6V5h2v6h6Z"
              />
            </svg>
            <span>{{ $t("project_settings.add_room") }}</span>
          </button>
        </div>
        <div class="project__settings__inner">
          <div v-for="room in projectDetails.project_rooms" class="room-single">
            <div class="room-single__info">
              <div class="room-single__name">{{ room.room?.name }}</div>
              <div class="room-single__buttons">
                <button
                  data-a11y-dialog-show="furniture-dialog"
                  @click.prevent="
                    showAddFurnitureDialog(
                      room.id,
                      room.room?.slug,
                      room.room_furnitures
                    )
                  "
                >
                  <img
                    src="@/assets/icons/admin/add-brown.svg"
                    :alt="$t('project_settings.icon_plus')"
                  />
                  {{ $t("project_settings.furnitures") }}
                </button>
                <button
                  data-a11y-dialog-show="params-dialog"
                  @click.prevent="
                    showRoomParametersDialog(room.id, room.parameters)
                  "
                >
                  <img
                    src="@/assets/icons/admin/add-brown.svg"
                    :alt="$t('project_settings.icon_plus')"
                  />
                  {{ $t("project_settings.parameters") }}
                </button>
                <button @click.prevent="showDeleteRoomDialog(room)">
                  <!--                                    <img src="@/assets/icons/admin/add-brown.svg" :alt="$t('project_settings.icon_plus')" />-->
                  <svg
                    id="delete_outline-24px"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="#ad6f69"
                  >
                    <path
                      id="Path_866"
                      data-name="Path 866"
                      d="M0,0H24V24H0Z"
                      fill="none"
                    />
                    <path
                      id="Path_867"
                      data-name="Path 867"
                      d="M6,21H18V7H6ZM8,9h8V19H8Zm7.5-5-1-1h-5l-1,1H5V6H19V4Z"
                    />
                  </svg>

                  {{ $t("project_settings.delete") }}
                </button>
              </div>
            </div>
            <div class="room-single__center">
              <div
                v-for="furniture in room.room_furnitures"
                class="furniture-item"
              >
                <button
                  class="furniture-item__close"
                  @click.prevent="
                    showDeleteFurnitureDialog(
                      room.id,
                      room.room_furnitures,
                      furniture
                    )
                  "
                >
                  <img
                    src="@/assets/icons/dialog-close.svg"
                    width="24"
                    height="24"
                    :alt="$t('project_settings.dialog_close')"
                  />
                </button>
                <div class="furniture-item__top">
                  <img :src="furniture.imagePath" :alt="furniture.name" />
                </div>
                <div class="furniture-item__bottom">
                  {{ furniture.name_pl ?? furniture.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <budget :project="projectDetails" />
      <lead-time :project="projectDetails" />
      <client-profile :project="projectDetails" />
    </section>
  </main>
  <add-room-modal
    :type="projectDetails.typeOfClient"
    ref="addRoomModal"
    @add-new-rooms="addNewRooms"
  />
  <add-furniture-modal
    ref="addFurnitureModal"
    @update-furnitures-and-params="updateFurnituresAndParams"
  />
  <room-parameters-modal ref="roomParametersModal" />
  <delete-room-modal ref="deleteRoomModal" />
  <delete-furniture-modal ref="deleteFurnitureModal" />
</template>

<script>
import { inject, onMounted, ref, watch } from "vue";
import UserLayout from "@/layouts/UserLayout";
import team from "@/components/architect/projectSettings/Team";
import { storeToRefs } from "pinia";
import { useProductStore } from "@/stores/product.store";
import Budget from "@/components/architect/projectSettings/Budget";
import LeadTime from "@/components/architect/projectSettings/LeadTime";
import axios from "axios";
import ApiHelper from "@/helpsers/api.helper";
import authHelper from "@/helpsers/auth.helper";
import router from "@/router";
import DeleteRoomModal from "@/components/architect/projectSettings/DeleteRoomModal";
import DeleteFurnitureModal from "@/components/architect/projectSettings/DeleteFurnitureModal";
import AddRoomModal from "@/components/architect/projectSettings/AddRoomModal";
import { useProjectSettingsStore } from "@/stores/user/architect/projectSettings.store";
import AddFurnitureModal from "@/components/architect/projectSettings/AddFurnitureModal";
import RoomParametersModal from "@/components/architect/projectSettings/RoomParametersModal";
import ClientProfile from "@/components/architect/projectSettings/ClientProfile";
import { useAuthStore } from "@/stores/auth.store";
import { Role } from "@/helpsers/role.helper";
import "@/css/admin-users.scss";
import { useRoute } from "vue-router";

export default {
  name: "ProjectSettings",
  components: {
    ClientProfile,
    RoomParametersModal,
    AddFurnitureModal,
    AddRoomModal,
    DeleteFurnitureModal,
    DeleteRoomModal,
    LeadTime,
    Budget,
    UserLayout,
    team,
  },
  setup() {
    const roomDialog = ref(),
      furnitureDialog = ref(),
      paramsDialog = ref();
    const auth = useAuthStore();
    const projectSettingsStore = useProjectSettingsStore();
    let isLoading = ref(false);
    let projectDetails = ref([]);
    const addRoomModal = ref();
    const addFurnitureModal = ref();
    const roomParametersModal = ref();
    const deleteRoomModal = ref();
    const deleteFurnitureModal = ref();
    const emitter = inject("emitter");
    const route = useRoute();

    projectSettingsStore.fetchFurnitures();

    async function fetchProjectDetails() {
      isLoading.value = true;

      axios
        .get(
          ApiHelper.getProjectDetailsUrl(router.currentRoute.value.params.id),
          {
            headers: authHelper.authHeader(),
          }
        )
        .then((result) => {
          projectDetails.value = result.data ?? {};
          isLoading.value = false;
        })
        .catch((errorResponse) => {
          if (errorResponse.response.data.code == "404")
            router.push({ path: "/not-found" });
          isLoading.value = false;
        });
    }

    fetchProjectDetails();
    projectSettingsStore.fetchRooms(projectDetails.value.typeOfClient);
    watch(
      () => projectDetails.value,
      () => {
        projectSettingsStore.fetchRooms(projectDetails.value.typeOfClient);
      }
    );

    function addNewRooms(roomsData) {
      if (roomsData && roomsData.length > 0) {
        for (let roomDataIndex in roomsData)
          projectDetails.value.project_rooms.push(roomsData[roomDataIndex]);
      }
    }
    function updateFurnituresAndParams(furnituresAndParams) {
      if (furnituresAndParams && furnituresAndParams["roomId"]) {
        for (let currentRoomIndex in projectDetails.value.project_rooms) {
          if (
            furnituresAndParams["roomId"] ==
            projectDetails.value.project_rooms[currentRoomIndex].id
          ) {
            projectDetails.value.project_rooms[
              currentRoomIndex
            ].room_furnitures =
              furnituresAndParams["data"]["room_furnitures"] ?? [];
            projectDetails.value.project_rooms[currentRoomIndex].parameters =
              furnituresAndParams["data"]["parameters"] ?? {};
          }
        }
      }
    }

    function showAddRoomDialog() {
      addRoomModal.value.showModal();
    }
    function showAddFurnitureDialog(roomId, roomSlug, allFurnitures) {
      addFurnitureModal.value.showModal(roomId, roomSlug, allFurnitures);
    }
    function showRoomParametersDialog(roomId, allParameters) {
      roomParametersModal.value.showModal(roomId, allParameters);
    }
    function showDeleteRoomDialog(roomData) {
      deleteRoomModal.value.showModal(roomData);
    }
    function showDeleteFurnitureDialog(roomId, allFurnitures, furnitureData) {
      deleteFurnitureModal.value.showModal(
        roomId,
        allFurnitures,
        furnitureData
      );
    }

    emitter.on("refreshProjectDetails", () => {
      fetchProjectDetails();
    });

    return {
      isLoading,
      projectDetails,
      addRoomModal,
      showAddRoomDialog,
      addFurnitureModal,
      showAddFurnitureDialog,
      roomParametersModal,
      showRoomParametersDialog,
      deleteRoomModal,
      showDeleteRoomDialog,
      deleteFurnitureModal,
      showDeleteFurnitureDialog,
      ApiHelper,
      addNewRooms,
      updateFurnituresAndParams,
      auth,
      Role,
      route,
    };
  },
};
</script>

<style lang="scss" scoped>
main.app-container {
  background-color: white;
}
.profile {
  background-color: white !important;
}

.complete-later {
  color: #ff7366;
  cursor: pointer;
  &:hover {
    color: #5d5d5d;
  }
}

.room-single {
  &__info {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
  }
  &__buttons {
    button {
      cursor: pointer;
    }
  }

  &__name {
    max-width: 280px;
    text-align: center;
    overflow: auto;
    display: block;
    height: max-content;
  }
}
</style>
